import { FC } from 'react';
import { useBanner, MaintenanceBanner } from '@unbooking/ui-kit';
import { Button, Main, Tip } from '@components';
import { URLS } from '@common/constants';
import { useGlobalContext } from '@context';
import {
  ViewBookingsIcon,
  CollaborateIcon,
  TaskIcon,
  SyncIcon,
  UserIcon,
  MarkerInfoIcon,
  CarIcon,
  GearIcon,
  ProjectIcon,
} from '@assets/svg';
import css from './styles.module.scss';
import { useRepository } from '@context';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FmsSyncInfo, FmsSyncTasks } from '@common/interfaces';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';

const REFETCH_INTERVAL = 10000;

const GET_PARAMS = {
  refetchInterval: REFETCH_INTERVAL,
  refetchIntervalInBackground: true,
};

const renderSyncStatus = (info?: FmsSyncInfo) => {
  if (!info) return '—';
  if (info.in_progress) {
    return 'Sync in progress';
  }
  if (info.last_synced_at) {
    const date = parseISO(info.last_synced_at);
    return `Latest sync ${format(date, 'dd/MM')} at ${format(date, 'HH:mm')}`;
  }
  return 'Not synced yet';
};

const AdminAreaEntryPage: FC = () => {
  const {
    user: { is_do_superuser },
  } = useGlobalContext();

  const { bannerMessage, closeBanner } = useBanner({
    switchName: 'maintenance_notice_workshops',
    switchesUrl: `${process.env.REACT_APP_HBH_API}/v2/core/feature-flags/`,
    monolithApiKey: String(process.env.REACT_APP_HBH_KEY),
  });

  const { facilityRepository } = useRepository();
  const queryClient = useQueryClient();

  const { data: assetsInfo } = useQuery<FmsSyncInfo>(
    ['assets-info', FmsSyncTasks.ASSETS],
    () => facilityRepository.getSyncFmsData(FmsSyncTasks.ASSETS),
    GET_PARAMS
  );

  const { data: jobcardsInfo } = useQuery<FmsSyncInfo>(
    ['jobcards-info', FmsSyncTasks.JOBCARDS],
    () => facilityRepository.getSyncFmsData(FmsSyncTasks.JOBCARDS),
    GET_PARAMS
  );

  const { data: mechanicsInfo } = useQuery<FmsSyncInfo>(
    ['mechanics-info', FmsSyncTasks.MECHANICS],
    () => facilityRepository.getSyncFmsData(FmsSyncTasks.MECHANICS),
    GET_PARAMS
  );

  const { data: depotsInfo } = useQuery<FmsSyncInfo>(
    ['depots-info', FmsSyncTasks.DEPOTS],
    () => facilityRepository.getSyncFmsData(FmsSyncTasks.DEPOTS),
    GET_PARAMS
  );

  const { data: customersInfo } = useQuery<FmsSyncInfo>(
    ['customers-info', FmsSyncTasks.CUSTOMERS],
    () => facilityRepository.getSyncFmsData(FmsSyncTasks.CUSTOMERS),
    GET_PARAMS
  );

  const { mutate: syncAssets } = useMutation(() => facilityRepository.syncFmsData(FmsSyncTasks.ASSETS), {
    onSuccess: () => {
      toast.success('FMS sync has started');
      queryClient.invalidateQueries(['assets-info', FmsSyncTasks.ASSETS]);
    },
  });

  const { mutate: syncJobcards } = useMutation(() => facilityRepository.syncFmsData(FmsSyncTasks.JOBCARDS), {
    onSuccess: () => {
      toast.success('FMS sync has started');
      queryClient.invalidateQueries(['jobcards-info', FmsSyncTasks.JOBCARDS]);
    },
  });

  const { mutate: syncMechanics } = useMutation(() => facilityRepository.syncFmsData(FmsSyncTasks.MECHANICS), {
    onSuccess: () => {
      toast.success('FMS sync has started');
      queryClient.invalidateQueries(['mechanics-info', FmsSyncTasks.MECHANICS]);
    },
  });

  const { mutate: syncDepots } = useMutation(() => facilityRepository.syncFmsData(FmsSyncTasks.DEPOTS), {
    onSuccess: () => {
      toast.success('FMS sync has started');
      queryClient.invalidateQueries(['depots-info', FmsSyncTasks.DEPOTS]);
    },
  });

  const { mutate: syncCustomers } = useMutation(() => facilityRepository.syncFmsData(FmsSyncTasks.CUSTOMERS), {
    onSuccess: () => {
      toast.success('FMS sync has started');
      queryClient.invalidateQueries(['customers-info', FmsSyncTasks.CUSTOMERS]);
    },
  });

  const fmsBlocks = [
    {
      task: FmsSyncTasks.ASSETS,
      title: 'FMS Assets',
      icon: <CarIcon />,
      buttonText: 'Sync now',
      info: assetsInfo,
      onSync: syncAssets,
    },
    {
      task: FmsSyncTasks.JOBCARDS,
      title: 'FMS Jobcards',
      icon: <ProjectIcon />,
      buttonText: 'Sync now',
      info: jobcardsInfo,
      onSync: syncJobcards,
    },
    {
      task: FmsSyncTasks.MECHANICS,
      title: 'FMS Mechanics',
      icon: <GearIcon />,
      buttonText: 'Sync now',
      info: mechanicsInfo,
      onSync: syncMechanics,
    },
    {
      task: FmsSyncTasks.DEPOTS,
      title: 'FMS Depot ID',
      icon: <MarkerInfoIcon />,
      buttonText: 'Sync now',
      info: depotsInfo,
      onSync: syncDepots,
    },
    {
      task: FmsSyncTasks.CUSTOMERS,
      title: 'FMS Customers',
      icon: <UserIcon />,
      buttonText: 'Sync now',
      info: customersInfo,
      onSync: syncCustomers,
    },
  ];

  return (
    <Main withoutPaddings background='admin'>
      {bannerMessage ? <MaintenanceBanner message={bannerMessage} onClose={closeBanner} /> : null}
      <div className={css.container}>
        <div className={css.content}>
          <div className={css.title}>
            WFP <b>Workshop</b>
            <br /> Admin Area
          </div>
          <div className={css.blocks}>
            <div className={css.block}>
              <div className={css.label}>User Management</div>
              <div className={css.description}>Create new users and manage their access rights and permissions.</div>
              <div className={css.bottom}>
                <Button
                  className={css.button}
                  link={URLS.ADMIN_AREA_USERS}
                  iconR={<ViewBookingsIcon />}
                  text='Manage users'
                  variant='forest'
                />
              </div>
            </div>
            <div className={css.block}>
              <div className={css.label}>Workshop Facilities Management</div>
              <div className={css.description}>Create new workshop facilities and update facilities information.</div>
              <div className={css.bottom}>
                <Tip className={css.tip} text='Only Super Users can access this section' isVisible={!is_do_superuser}>
                  <Button
                    className={css.button}
                    iconR={<TaskIcon />}
                    link={URLS.ADMIN_AREA_FACILITIES}
                    text='Manage workshops'
                    variant='lochmara'
                    disabled={!is_do_superuser}
                  />
                </Tip>
              </div>
            </div>
            <div className={css.block}>
              <div className={css.label}>Customer Area Management</div>
              <div className={css.description}>
                Create and manage customer areas where users can track their requests.
              </div>
              <div className={css.bottom}>
                <Tip className={css.tip} text='Only Super Users can access this section' isVisible={!is_do_superuser}>
                  <Button
                    className={css.button}
                    iconR={<CollaborateIcon />}
                    link={URLS.ADMIN_AREA_LOCATIONS}
                    text='Manage customer areas'
                    variant='supernova'
                    disabled={!is_do_superuser}
                  />
                </Tip>
              </div>
            </div>
          </div>
        </div>
        <div className={css.sidebar}>
          <div className={css.sidebarTitle}>Workshop FMS Syncronization</div>
          {fmsBlocks.map(({ task, title, icon, buttonText, info, onSync }) => {
            return (
              <div key={task} className={css.infoBlock}>
                <div className={css.subtitle}>
                  {icon} {title}
                </div>
                <div className={css.date}>{renderSyncStatus(info)}</div>
                <Button
                  className={css.syncButton}
                  text={buttonText}
                  iconR={<SyncIcon />}
                  variant='transparent'
                  disabled={Boolean(info?.in_progress)}
                  onClick={() => onSync()}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Main>
  );
};

export default AdminAreaEntryPage;
